/**
 * `/` — authenticated landing page.
 *
 * Lists the teams the current account is an active member of. Click a
 * team → `/teams/:teamId` (the cycle list). New accounts with zero
 * memberships see a CTA pointing at the (Stream C) onboarding wizard.
 *
 * IA Phase 2 (chunk IA-2.3): on mount we resolve the active workspace
 * via `resolveInitialWorkspace` and persist it to localStorage so the
 * `<WorkspaceSwitcher/>` pill in the AppShell renders the correct
 * workspace once it mounts. Per the implementation plan §4.6 the page
 * does NOT auto-redirect to `/teams/<active>` — the team list overview
 * is the load-bearing v0.1 landing surface; the rebuild as an
 * "all-teams calendar" is deferred. The resolver write is the only
 * side-effect.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { apiFetch } from "../api/client";
import type { paths } from "../api/schema.gen";
import { useWorkspaces } from "../api/useWorkspaces";
import { useBreadcrumbs } from "../components/Breadcrumbs";
import { DeleteTeamDialog } from "../components/DeleteTeamDialog";
import { resolveCreateTeamGate } from "../components/WorkspaceSwitcher";
import { Button, EmptyState, ErrorState, PageLead, Skeleton } from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import {
  getActiveWorkspaceId,
  resolveInitialWorkspace,
  setActiveWorkspaceId,
} from "../lib/activeWorkspace";
import { addDaysIso, mondayOf } from "../lib/rosterDatesWindow";
import { workspacesKey } from "../lib/workspacesKey";
import { GreetingBand } from "./home/GreetingBand";
import { TeamTile } from "./home/TeamTile";
import { HomeLegend } from "./home/TileParts";
import { needChips, totalNeeds } from "./home/homeOverview";
import { useHomeMemberFacts, useTeamOverview } from "./home/useTeamOverview";
import { useToday } from "./home/useToday";
import { AdminAllTeamsTable } from "./settings/AdminAllTeamsTable";

type Team = paths["/api/teams"]["get"]["responses"]["200"]["content"]["application/json"][number];

export function HomePage() {
  const { account, accountId, loading: authLoading } = useAuth();
  useBreadcrumbs([{ label: "Home" }]);
  const queryClient = useQueryClient();

  const teamsQuery = useQuery({
    queryKey: ["teams", accountId],
    enabled: Boolean(accountId),
    queryFn: () => apiFetch<Team[]>("/api/teams"),
  });

  // Issue #1275 — per-card delete affordance. `teamToDelete` doubles as
  // the dialog's open state; the server enforces owner-only, the card
  // only offers the affordance on lead-role teams.
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const onTeamDeleted = () => {
    if (teamToDelete && getActiveWorkspaceId() === teamToDelete.id) {
      // The active workspace just vanished — clear it so the switcher
      // re-resolves instead of pointing at a deleted team.
      setActiveWorkspaceId(null);
    }
    setTeamToDelete(null);
    queryClient.invalidateQueries({ queryKey: ["teams", accountId] });
    queryClient.invalidateQueries({ queryKey: workspacesKey(accountId) });
  };

  // IA-2.3 — workspace resolver on first mount. The AppShell already
  // runs the same resolver, but the HomePage runs it independently so
  // a deep-link to `/` that bypasses the AppShell's mount cycle
  // (e.g. a fresh browser tab opening directly to `/`) still gets
  // the localStorage write. The shared `useWorkspaces` cache (keyed
  // by `accountId`) makes the second call free — no extra network.
  const workspacesQuery = useWorkspaces();
  const resolverRanRef = useRef(false);
  useEffect(() => {
    if (resolverRanRef.current) return;
    if (!workspacesQuery.data) return;
    resolverRanRef.current = true;
    const resolved = resolveInitialWorkspace(workspacesQuery.data, null);
    if (resolved !== null) {
      setActiveWorkspaceId(resolved);
    } else if (workspacesQuery.data.length === 0) {
      setActiveWorkspaceId(null);
    }
  }, [workspacesQuery.data]);

  const teamCount = teamsQuery.data?.length ?? 0;
  // #2060 — the master account: an admin account with no team of its own.
  // Its Home is every team on Tarii, each opening read-only. An admin who
  // does lead or belong to teams keeps this page as it was (the all-teams
  // table stays on settings/admin and in the team switcher).
  const isMasterHome =
    account?.is_admin === true && teamsQuery.isSuccess && (teamsQuery.data?.length ?? 0) === 0;
  const displayName = authLoading ? "there" : account?.display_name || account?.email || "there";

  // #1909 — a lead with teams comes here to pick one, and sometimes to add
  // another: Create team sits at the right of "Your teams". Never offered to
  // a member-only account. Same gate as the workspace switcher's "Create a
  // new team" row: permissive until `/api/account/me` resolves, then
  // disabled with the plan-cap reason (the backend still refuses past it).
  const leadsATeam = (teamsQuery.data ?? []).some(
    (team) => team.role === "lead" || team.role === "co_lead",
  );
  const createGate = account
    ? resolveCreateTeamGate(
        account.max_teams ?? null,
        account.current_team_count ?? 0,
        account.plan,
      )
    : { disabled: false, reason: "" };

  // #2640 — the tiles' facts. Each read is its own query and none blocks the
  // list. `today` comes from `useToday`, which re-reads the date when the tab
  // regains focus, so the tiles and the greeting move to a new day together.
  const today = useToday();
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new day gives a new `now`.
  const now = useMemo(() => new Date(), [today]);
  const monday = mondayOf(today);
  const teams = teamsQuery.data ?? [];
  const overview = useTeamOverview(monday, teams.length > 0 && !isMasterHome);
  const hasMemberTeam = teams.some((team) => team.role === "member");
  const memberFacts = useHomeMemberFacts(monday, addDaysIso(monday, 13), hasMemberTeam);
  const needsTotal = totalNeeds(
    teams.map((team) =>
      team.team_kind === "tasks" ? [] : needChips(team.role, overview.byTeam.get(team.id)),
    ),
  );

  if (isMasterHome) {
    return (
      <div className="flex min-w-0 flex-col gap-8" data-testid="home-all-teams">
        <PageLead
          title={`Hello, ${displayName}`}
          subtitle="Every team on Tarii. Open one to see it read only, as its lead sees it."
        />
        <AdminAllTeamsTable
          withStatus
          description="This week's status for each team, from its solves. Nothing here changes a team."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <GreetingBand
        name={authLoading ? null : account?.display_name || account?.email || null}
        total={needsTotal}
        now={now}
      />

      <section className="flex flex-col gap-4">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-lg font-semibold text-fg">Your teams</h2>
            {teamCount > 0 ? <HomeLegend /> : null}
          </div>
          {leadsATeam ? (
            createGate.disabled ? (
              <div className="flex flex-col gap-1 sm:items-end">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled
                  aria-describedby="home-create-team-reason"
                  data-testid="home-create-team"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Create team
                </Button>
                <p
                  id="home-create-team-reason"
                  className="max-w-xs text-xs text-fg-subtle sm:text-right"
                  data-testid="home-create-team-reason"
                >
                  {createGate.reason}
                </p>
              </div>
            ) : (
              <Button asChild variant="secondary" size="sm" data-testid="home-create-team">
                <Link to="/onboarding">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Create team
                </Link>
              </Button>
            )
          ) : null}
        </header>

        {teamsQuery.isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="hidden h-64 lg:block" />
          </div>
        ) : teamsQuery.isError ? (
          <ErrorState
            title="Couldn't load your teams"
            message={teamsQuery.error instanceof Error ? teamsQuery.error.message : undefined}
            onRetry={() => teamsQuery.refetch()}
          />
        ) : teamsQuery.data && teamsQuery.data.length > 0 ? (
          <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {teamsQuery.data.map((team) => (
              <li key={team.id} className="min-w-0">
                <TeamTile
                  team={team}
                  row={overview.byTeam.get(team.id)}
                  loading={overview.isLoading}
                  monday={monday}
                  today={today}
                  memberFacts={memberFacts}
                  onDelete={team.role === "lead" ? () => setTeamToDelete(team) : undefined}
                />
              </li>
            ))}
          </ul>
        ) : (
          /* #1720 — this state used to send everyone to `/onboarding`,
             i.e. found a new tenant, which quietly funnelled invited
             members into creating their own team. `<HomeEntryGate/>`
             now routes a zero-team account to the `/start` chooser
             before this page paints, so in practice this branch is
             reached only in the window where the workspace query is
             still resolving — it points at the same chooser so the
             branch is never silently defaulted, even in that window. */
          <EmptyState
            icon={Plus}
            title="You're not on any teams yet"
            description="Set up a team of your own, or join one you've been invited to — it takes one answer either way."
            action={
              <Button asChild size="sm">
                <Link to="/start">Choose how to start</Link>
              </Button>
            }
          />
        )}
      </section>

      <DeleteTeamDialog
        team={teamToDelete ? { id: teamToDelete.id, name: teamToDelete.name } : null}
        open={teamToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setTeamToDelete(null);
        }}
        onDeleted={onTeamDeleted}
      />
    </div>
  );
}
